import React from 'react';
import { Box, Typography, Button, Stack, Paper, alpha } from '@mui/material';
import {
  Inbox as InboxIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CloudOff as CloudOffIcon,
  ErrorOutline as ErrorIcon,
  FolderOff as FolderOffIcon
} from '@mui/icons-material';

type EmptyStateVariant =
  | 'applications'
  | 'jobs'
  | 'candidates'
  | 'documents'
  | 'search'
  | 'calls'
  | 'analytics'
  | 'generic'
  | 'error'
  | 'offline'
  | 'folder';

interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  icon?: React.ReactNode;
  compact?: boolean;
}

const variantConfig: Record<EmptyStateVariant, {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  color: string;
}> = {
  applications: {
    icon: <InboxIcon />,
    title: 'No applications yet',
    description: 'Applications will appear here once candidates start applying to your job postings.',
    actionLabel: 'Post a Job',
    color: '#4A3AFF'
  },
  jobs: {
    icon: <WorkIcon />,
    title: 'No jobs posted',
    description: 'Start attracting talent by creating your first job posting.',
    actionLabel: 'Create Job',
    color: '#10B981'
  },
  candidates: {
    icon: <PersonIcon />,
    title: 'No candidates found',
    description: 'There are no candidates matching your current filters.',
    actionLabel: 'Clear Filters',
    color: '#8B5CF6'
  },
  documents: {
    icon: <DescriptionIcon />,
    title: 'No documents',
    description: 'Upload resumes or documents to get started with AI-powered analysis.',
    actionLabel: 'Upload Files',
    color: '#F59E0B'
  },
  search: {
    icon: <SearchIcon />,
    title: 'No results found',
    description: 'Try adjusting your search terms or filters to find what you\'re looking for.',
    actionLabel: 'Clear Search',
    color: '#6366F1'
  },
  calls: {
    icon: <PhoneIcon />,
    title: 'No call logs',
    description: 'AI screening calls will appear here once you start initiating candidate calls.',
    actionLabel: 'Start Screening',
    color: '#EC4899'
  },
  analytics: {
    icon: <AnalyticsIcon />,
    title: 'No data available',
    description: 'Analytics will populate as you start receiving applications and making hires.',
    color: '#14B8A6'
  },
  generic: {
    icon: <InboxIcon />,
    title: 'Nothing here yet',
    description: 'This section is empty. Check back later or take action to add items.',
    color: '#64748B'
  },
  error: {
    icon: <ErrorIcon />,
    title: 'Something went wrong',
    description: 'We encountered an error loading this content. Please try again.',
    actionLabel: 'Try Again',
    color: '#EF4444'
  },
  offline: {
    icon: <CloudOffIcon />,
    title: 'You\'re offline',
    description: 'Please check your internet connection and try again.',
    actionLabel: 'Retry',
    color: '#6B7280'
  },
  folder: {
    icon: <FolderOffIcon />,
    title: 'Folder is empty',
    description: 'This folder doesn\'t contain any items yet.',
    color: '#78716C'
  }
};

/**
 * Empty State component for displaying when there's no data
 *
 * Usage:
 * <EmptyState
 *   variant="applications"
 *   onAction={() => navigate('/admin/jobs/post')}
 * />
 *
 * Or with custom content:
 * <EmptyState
 *   title="Custom Title"
 *   description="Custom description text"
 *   icon={<CustomIcon />}
 *   actionLabel="Do Something"
 *   onAction={handleAction}
 * />
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'generic',
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  icon,
  compact = false
}) => {
  const config = variantConfig[variant];

  const displayTitle = title || config.title;
  const displayDescription = description || config.description;
  const displayActionLabel = actionLabel || config.actionLabel;
  const displayIcon = icon || config.icon;
  const accentColor = config.color;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: compact ? 4 : 8,
        px: 3,
        textAlign: 'center'
      }}
    >
      {/* Illustrated Icon Container */}
      <Box
        sx={{
          position: 'relative',
          mb: 3
        }}
      >
        {/* Background circles for depth */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: compact ? 100 : 140,
            height: compact ? 100 : 140,
            borderRadius: '50%',
            bgcolor: alpha(accentColor, 0.08),
            animation: 'pulse 3s ease-in-out infinite'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: compact ? 80 : 110,
            height: compact ? 80 : 110,
            borderRadius: '50%',
            bgcolor: alpha(accentColor, 0.12)
          }}
        />

        {/* Icon */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: compact ? 64 : 80,
            height: compact ? 64 : 80,
            borderRadius: '50%',
            bgcolor: alpha(accentColor, 0.15),
            color: accentColor,
            '& .MuiSvgIcon-root': {
              fontSize: compact ? 32 : 40
            }
          }}
        >
          {displayIcon}
        </Box>
      </Box>

      {/* Text Content */}
      <Typography
        variant={compact ? 'h6' : 'h5'}
        fontWeight={600}
        color="text.primary"
        gutterBottom
      >
        {displayTitle}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          maxWidth: 400,
          mb: displayActionLabel || secondaryActionLabel ? 3 : 0,
          lineHeight: 1.6
        }}
      >
        {displayDescription}
      </Typography>

      {/* Actions */}
      {(displayActionLabel || secondaryActionLabel) && (
        <Stack direction="row" spacing={2}>
          {displayActionLabel && onAction && (
            <Button
              variant="contained"
              startIcon={variant === 'error' || variant === 'offline' ? <RefreshIcon /> : <AddIcon />}
              onClick={onAction}
              sx={{
                bgcolor: accentColor,
                '&:hover': {
                  bgcolor: alpha(accentColor, 0.9)
                },
                px: 3
              }}
            >
              {displayActionLabel}
            </Button>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <Button
              variant="outlined"
              onClick={onSecondaryAction}
              sx={{
                borderColor: alpha(accentColor, 0.5),
                color: accentColor,
                '&:hover': {
                  borderColor: accentColor,
                  bgcolor: alpha(accentColor, 0.05)
                }
              }}
            >
              {secondaryActionLabel}
            </Button>
          )}
        </Stack>
      )}

      {/* CSS Animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            50% { transform: translate(-50%, -50%) scale(1.05); opacity: 0.8; }
          }
        `}
      </style>
    </Box>
  );
};

/**
 * Empty State wrapped in a Paper container
 */
export const EmptyStateCard: React.FC<EmptyStateProps> = (props) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <EmptyState {...props} />
    </Paper>
  );
};

export default EmptyState;
